import { isEnum, isMultienum } from 'customer-data-objects/property/PropertyIdentifier';
import { parseMultiEnumValue } from 'customer-data-property-utils/parseMultiEnumValue';
import { useCallback, useMemo, useState } from 'react';
import { difference, getConditionalPropertyDependencies } from '../../form/utils';
import ConditionalPropertiesPanel from '../../panels/ConditionalPropertiesPanel';
import { getOwnProperty } from '../../utils/getOwnProperty';
import { getConditionalPropertyOptionsRulesWithDependentErrors } from '../conditionalPropertyOptionsRules/getConditionalPropertyOptionsRulesWithDependentErrors';
import { getActiveRules, getParsedPropertyValueAsArray } from '../conditionalPropertyOptionsRules/utils';
import { useAllPropertyValues } from './useAllPropertyValues';
import { useConditionalPropertyOptionsRulesForObjectType } from './useConditionalPropertyOptionsRulesForObjectType';
import { useConditionalPropertyRulesForObjectType } from './useConditionalPropertyRulesForObjectType';
import { usePropertyDefinitions } from './usePropertyDefinitions';
import { jsx as _jsx } from "react/jsx-runtime";
const propertyNameToObject = propertyName => ({
  propertyName
});
const initialState = {
  isOpen: false,
  values: {},
  properties: []
};
const useConditionalPropertiesPanel = ({
  enableConditionalPropertiesPanel = true,
  conditionalPropertyRules,
  onChange,
  onCancel,
  property,
  objectTypeId,
  objectId,
  getAdditionalPropertyValue,
  showAutoSaveText,
  standaloneUsage,
  value
}) => {
  // PropertyInputV2 will trigger Conditional Properties Panel only for enum properties.
  // As of June 2023, only enum properties can have conditional rules. So, bypassing all panel checks if current property is not enum
  // While expanding conditional rules to other property types, UX should be clarified on handling text properties as
  // panel would need to be triggered without a debounce to avoid multiple onChange calls to host apps
  const shouldCheckForConditionalPropertiesPanel = isEnum(property) && enableConditionalPropertiesPanel;
  const shouldApplyConditionalPropertyOptionsRules = shouldCheckForConditionalPropertiesPanel;
  const {
    // _ to avoid shadowing the memoized version which falls back to []
    data: _conditionalPropertyRulesForObjectType,
    loading: conditionalPropertyRulesLoading,
    error: conditionalPropertyRulesError
  } = useConditionalPropertyRulesForObjectType(objectTypeId, {
    skip:
    // don't fetch conditional rules if given via args
    conditionalPropertyRules !== undefined || !shouldCheckForConditionalPropertiesPanel
  });
  const {
    data: conditionalPropertyOptionsRules,
    loading: conditionalPropertyOptionsRulesLoading,
    error: conditionalPropertyOptionsRulesError
  } = useConditionalPropertyOptionsRulesForObjectType(objectTypeId, {
    skip: !shouldApplyConditionalPropertyOptionsRules
  });
  const {
    // _ to avoid shadowing the memoized version which falls back to {}
    data: _allPropertyDefinitionsForObjectType,
    loading: allPropertyDefinitionsLoading,
    error: allPropertyDefinitionsError
  } = usePropertyDefinitions(objectTypeId, {
    skip: !shouldApplyConditionalPropertyOptionsRules
  });
  const conditionalPropertyRulesForObjectType = useMemo(() => {
    return conditionalPropertyRules || _conditionalPropertyRulesForObjectType || [];
  }, [_conditionalPropertyRulesForObjectType, conditionalPropertyRules]);
  const conditionalPropertyOptionsRulesForProperty = useMemo(() => {
    return conditionalPropertyOptionsRules ? conditionalPropertyOptionsRules.filter(rule => rule.controllingField.fieldName === property.name) : [];
  }, [conditionalPropertyOptionsRules, property.name]);
  const allPropertyDefinitionsForObjectType = useMemo(() => _allPropertyDefinitionsForObjectType || {}, [_allPropertyDefinitionsForObjectType]);
  const [{
    isOpen,
    values,
    properties
  }, setState] = useState(initialState);
  const {
    data: allPropertyValues,
    loading: allPropertyValuesLoading,
    error: allPropertyValuesError
  } = useAllPropertyValues({
    objectId,
    objectTypeId,
    skip: !shouldApplyConditionalPropertyOptionsRules
  });
  const propertyDefinitionByName = useMemo(() => {
    return {
      [property.name]: property
    };
  }, [property]);
  const closePanel = useCallback(() => {
    setState({
      isOpen: false,
      values: {},
      properties: []
    });
  }, []);
  const openPanel = useCallback((_values, _properties) => {
    setState({
      isOpen: true,
      values: _values,
      properties: _properties
    });
  }, []);
  const maybeOpenConditionalPropertiesPanel = useCallback(changes => {
    // for purposes of calculating what CP rules this edit triggered:
    // 1. only consider values changed as part of this edit
    // 2. when editing a multi-enum, only consider newly-added values.
    //    (otherwise, existing values with CP rules would trigger the CP panel
    //    to appear repeatedly when other values are added/removed)
    const getFormValue = propertyName => {
      const nextValue = getOwnProperty(changes, propertyName);
      if (propertyName !== property.name || !isMultienum(property) || !nextValue || !value) {
        return nextValue;
      }
      const prevValues = new Set(parseMultiEnumValue(value));
      const nextValues = new Set(parseMultiEnumValue(nextValue));
      const added = difference(nextValues, prevValues);
      // callback runs each time an option is selected, so we would expect at
      // most one new value added at a time. on deselection, the `added` array
      // will be empty and we don't have to show CPP
      return added[0];
    };
    const getFormValueWithAdditionalValues = propertyName => {
      if (Object.prototype.hasOwnProperty.call(changes, propertyName)) {
        return changes[propertyName];
      }
      const additionalPropertyValue = getAdditionalPropertyValue(propertyName, objectId, objectTypeId);
      if (additionalPropertyValue !== undefined) {
        return additionalPropertyValue;
      }
      return allPropertyValues ? getOwnProperty(allPropertyValues, propertyName) : undefined;
    };
    const conditionalPropertyDependencies = getConditionalPropertyDependencies(objectTypeId, getFormValue, propertyDefinitionByName, conditionalPropertyRulesForObjectType);
    const activeConditionalPropertyOptionsRules = getActiveRules(getParsedPropertyValueAsArray(property, changes[property.name]), conditionalPropertyOptionsRulesForProperty);
    const dependentFieldValues = activeConditionalPropertyOptionsRules.reduce((acc, rule) => {
      acc[rule.dependentFieldName] = getFormValueWithAdditionalValues(rule.dependentFieldName);
      return acc;
    }, {});
    const activeConditionalPropertyOptionsRulesWithErrors = getConditionalPropertyOptionsRulesWithDependentErrors(activeConditionalPropertyOptionsRules, allPropertyDefinitionsForObjectType, dependentFieldValues);
    const activeControllingProperties = Object.keys(conditionalPropertyDependencies);
    const shouldOpenPanel = activeControllingProperties.length > 0 || activeConditionalPropertyOptionsRulesWithErrors.length > 0;
    if (shouldOpenPanel) {
      // if there are no CP dependents, we just show panel with root input
      // and form takes care of adding CPO dependents to the tree
      const initialProperties = activeControllingProperties.length > 0 ? activeControllingProperties.map(propertyNameToObject) : [{
        propertyName: property.name
      }];
      openPanel(changes, initialProperties);
    } else {
      onChange(changes);
    }
  }, [allPropertyDefinitionsForObjectType, allPropertyValues, conditionalPropertyOptionsRulesForProperty, conditionalPropertyRulesForObjectType, getAdditionalPropertyValue, openPanel, objectId, objectTypeId, onChange, property, propertyDefinitionByName, value]);
  if (!shouldCheckForConditionalPropertiesPanel) {
    return {
      maybeOpenConditionalPropertiesPanel: onChange,
      conditionalPropertiesPanel: null,
      loading: false,
      error: undefined
    };
  }
  const conditionalPropertiesPanel = isOpen ? /*#__PURE__*/_jsx(ConditionalPropertiesPanel, {
    properties: properties,
    onApply: changes => {
      onChange(changes);
      closePanel();
    },
    onCancel: () => {
      if (onCancel) {
        onCancel();
      }
      closePanel();
    },
    objectTypeId: objectTypeId,
    objectId: objectId,
    getAdditionalPropertyValue: getAdditionalPropertyValue,
    triggerPropertyDefinition: property,
    defaultValues: values,
    showAutoSaveText: showAutoSaveText,
    standaloneUsage: standaloneUsage
  }) : null;
  return {
    conditionalPropertiesPanel,
    maybeOpenConditionalPropertiesPanel,
    loading: conditionalPropertyRulesLoading || conditionalPropertyOptionsRulesLoading || allPropertyDefinitionsLoading || allPropertyValuesLoading,
    error: conditionalPropertyRulesError || conditionalPropertyOptionsRulesError || allPropertyDefinitionsError || allPropertyValuesError
  };
};
export default useConditionalPropertiesPanel;